import * as React from "react";
import { useLocation } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import posthog from "posthog-js";
import {
  aliasUser,
  initPosthog,
  setUserId,
  setUserProfile,
  trackEvent,
} from "@/services/posthog";
import { useAppSelector } from "@/hooks/redux";
import { formatDuration } from "@/utils/datetime";


const PosthogProvider = ({ children }: { children: ReactNode }) => {
  const { userData } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const [startTime, setStartTime] = useState(Date.now());

  useEffect(() => {
    const initAnalytics = () => {
      try {
        initPosthog();

        // Only set user data if we have a user and tracking is enabled
        if (userData) {
          const anonymousId = posthog?.get_distinct_id();
          const userId = userData?.user?.id;

          if (userId && anonymousId !== userId) {
            aliasUser(anonymousId, userId);
          }

          if (userId) {
            setUserId(userId);
            setUserProfile({
              ...userData?.user,
            });
          }
        }
      } catch (error) {
        console.error("PostHog initialization error:", error);
      }
    };

    initAnalytics();
  }, [userData]);

  useEffect(() => {

    // Track pageview using PostHog's standard $pageview event for Web Analytics
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      path: location.pathname,
    });

    const handlePageLoad = () => {
      setStartTime(Date.now());
      trackEvent(`Page Stay Start Duration: ${location.pathname}`, {
        page: location.pathname,
        timestamp: new Date().toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        }),
      });
    };

    const handlePageUnload = () => {
      const duration = Date.now() - startTime;
      trackEvent(`Page Stay End Duration: ${location.pathname}`, {
        page: location.pathname,
        duration: formatDuration(duration),
      });
    };

    handlePageLoad();
    window.addEventListener("beforeunload", handlePageUnload);

    return () => {
      window.removeEventListener("beforeunload", handlePageUnload);
      handlePageUnload();
    };
  }, [location.pathname]);

  return <React.Fragment>{children}</React.Fragment>;
};

export default PosthogProvider;
